// Os destinos do custo do cliente — a decomposição do áudio 1 (spec-022,
// [US-1]): "o custo total que o cliente tá pagando é esse, mais X vai pra
// gestora, X vai pra plataforma e X vai pro profissional".
//
// Os 3 baldes principais saem da MESMA regra do backend
// (`core/baldes_de_custo.py::baldes_do_custo`), espelhada em
// `utils/fee-lab-baldes.js`: a captura de investimentos é REPARTIDA entre
// plataforma e escritório pela premissa `repasse_plataforma`, e a assessoria é
// esse repasse mais o fee cobrado do cliente. Não-investimentos continua sendo
// leitura direta de `costBreak[regime]`; o cashback deixou de ser linha e passou
// a ser abatido dentro do balde da plataforma (owner, 2026-08-03).
//
// A lista é a ordem de exibição e, junto com o invariante Σ baldes == total
// (`utils/fee-lab-baldes.js`), o contrato do card: uma parcela nova no motor
// que não entrar aqui aparece como divergência declarada, nunca como total
// silenciosamente errado. ⚠️ O invariante é CEGO à divisão interna da captura
// (plataforma × assessoria somam a mesma coisa em qualquer proporção) — foi por
// isso que ele não pegou a divergência com o PDF; quem trava a divisão é o
// gabarito golden do unit.

import { KCOLOR } from '@modules/fee-fixo/constants/feeLab'

// Chaves dos baldes — usadas como `item-key` da tabela e nos testes.
export const BALDE_GESTORA = 'gestora'
export const BALDE_PLATAFORMA = 'plataforma'
export const BALDE_ASSESSORIA = 'assessoria'
export const BALDE_NAO_INVESTIMENTOS = 'nao_investimentos'
export const BALDE_CASHBACK = 'cashback'

/**
 * Os 3 baldes do discurso + as 2 linhas que fecham a conta.
 *
 * `campo` é a chave em `costBreak[regime]` das linhas de leitura direta (hoje,
 * só os não-investimentos).
 *
 * `principal: true` marca os 3 baldes da narrativa — não-investimentos é linha
 * de fechamento, e a UI a separa por isso.
 *
 * Cores: paleta por classe do design-system (`KCOLOR`), a mesma do donut por
 * classe do card. Nenhum hex novo entra aqui.
 */
// ⚠️ O cashback NÃO é linha desta lente desde 2026-08-03: ele é abatido dentro
// do balde da plataforma (`utils/fee-lab-baldes.js`), e os destinos saem
// líquidos. Reintroduzi-lo aqui contaria a devolução duas vezes — uma dentro da
// plataforma e outra na própria linha — e a soma deixaria de reproduzir o
// total. Quem mostra a devolução como parcela própria é a lente POR COMPONENTE.
export const BALDES = [
  {
    k: BALDE_GESTORA,
    principal: true,
    label: 'Gestora do fundo',
    // Nome curto, para a frase do resumo — na tabela cabe o rótulo inteiro,
    // numa frase corrida ele empurra o número-manchete para longe.
    curto: 'gestora',
    ajuda:
      'O que fica com quem gere o produto — a taxa de administração menos o que a plataforma captura dela. Cai quando a carteira migra para produtos mais baratos.',
    color: KCOLOR.fa,
  },
  {
    k: BALDE_PLATAFORMA,
    principal: true,
    label: 'Plataforma · investimentos',
    curto: 'plataforma',
    ajuda:
      // Sem o marcador da decisão ([D8]): este texto é TOOLTIP, e o nome de uma
      // decisão nossa não diz nada a quem está com o cliente na frente. O
      // referente que o marcador carregava vira FATO na frase — a taxa entra no
      // custo publicado NAQUELE canal —, e é o que o leitor precisa saber,
      // inclusive porque este mesmo tooltip aparece na consultoria, onde a
      // parcela é zero.
      // ★ 11/08/2026: era "…entra no custo publicado do cliente". A taxa deixou
      // de ser acréscimo ao cliente — ela é retida de DENTRO do fee. O que a
      // frase precisa manter é o fato que a [US-4] travou (os dois são dinheiro
      // da plataforma, nenhum passa pelo escritório) e agora dizer de onde o
      // segundo sai, senão o leitor não acha os 15 bps que faltam ao lado.
      'O que a plataforma RETÉM do take rate de investimentos — a captura menos o que ela repassa ao escritório —, mais a taxa de plataforma, que no canal de assessoria ela retém de dentro do fee do profissional. Os dois são dinheiro que fica com ela; nenhum dos dois passa pelo escritório.',
    color: KCOLOR.eq,
  },
  {
    k: BALDE_ASSESSORIA,
    principal: true,
    label: 'Assessoria',
    curto: 'assessoria',
    ajuda:
      // ★ 11/08/2026: era "é o fee cobrado do cliente, e só ele". O balde vale
      // 45,0 enquanto a linha de fee ao lado mostra 60,0 — a frase contradizia
      // o número visível na mesma tela.
      'O que chega ao escritório. No comissionado é o repasse que a plataforma faz sobre a captura de investimentos — NÃO é zero, o profissional já é pago ali, por dentro do rebate. No fee-based é o fee cobrado do cliente menos a taxa que a plataforma retém no canal de assessoria: o escritório abre mão do rebate e vive do que sobra do fee.',
    color: KCOLOR.ret,
  },
  // ★ 12/08/2026 (owner): a simulação passou a considerar só INVESTIMENTOS.
  // Mesma escolha da lente por componente: a linha continua declarada e some
  // pelo DADO (`ocultaSeZero`), porque cenário salvo antes da decisão tem 42/27
  // dentro do próprio total — apagá-la abriria um buraco na conferência de um
  // documento que está certo.
  {
    k: BALDE_NAO_INVESTIMENTOS,
    campo: 'ni',
    sinal: 1,
    principal: false,
    ocultaSeZero: true,
    label: 'Não-investimentos',
    curto: 'não-investimentos',
    ajuda:
      'Receita de câmbio, seguros e crédito, capturada pela plataforma. Fica em linha própria porque não depende da alocação da carteira. Fora do modelo desde 12/08/2026 — aparece apenas em análise salva antes dessa data.',
    color: KCOLOR.fi,
  },
]

// Divergência máxima tolerada entre a soma das linhas exibidas e o total do
// motor (bps). 0,5 bps é o mesmo limiar da spec ([US-1]) — folga de
// arredondamento, e não de modelagem: acima disso a tela declara a divergência
// em vez de exibir um total que as linhas não reproduzem.
export const TOLERANCIA_SOMA_BALDES_BPS = 0.5

// Tooltip da visão por destino no card de composição. Mora aqui, junto da
// lista de baldes que ele descreve, e não no `TOOLTIPS` do laboratório: as duas
// coisas mudam juntas — descrever um balde que a lista não tem é o modo de
// falha que o QA da 021 mediu no subtítulo do card ([G-9]).
export const TOOLTIP_BALDES =
  'Abre o custo total do bolso do cliente por destino: o que fica com a gestora do fundo, o que a plataforma retém sobre os investimentos e o que vai para a assessoria. A simulação considera só investimentos — câmbio, seguros e cartões ficam de fora dos dois lados da conta. Os valores são LÍQUIDOS: o cashback devolvido já está abatido do balde da plataforma, que é quem devolve. Os números são os da mesma simulação dos cards de regime (nada é recalculado aqui): o total é o do motor, e a soma dos destinos o reproduz.'

// Regime de cada lado da comparação.
export const REGIME_ANTES = 'comis'

/**
 * O CANAL comanda o lado "depois" ([D6] do plano da spec-022, [D8] a partir daqui).
 *
 * A tela comparava contra o consultor e o payload do PDF contra a assessoria.
 * Com `cb_dev = 1` os dois regimes são idênticos e ninguém nota; com
 * `cb_dev = 0,5` a mesma análise dá 119,52 (Δ −44,43) na tela e 125,12
 * (Δ −38,83) no documento — 5,6 bps de diferença no número-manchete, porque o
 * que separa `fee` de `cons` no motor é a fração do cashback devolvida.
 *
 * **[D8] O canal deixou de ser derivado e virou ESCOLHA do cenário.** O owner
 * pediu o seletor na simulação (assessor fee-based é o default) e decidiu que
 * o custo publicado segue essa escolha: na assessoria a taxa de plataforma
 * entra no total (134,52, guia p.21), na consultoria fica de fora (119,52). A
 * nota "taxa por cima" que a tela dava morreu onde o total passou a contê-la.
 *
 * ⚠️ **Estes VALORES são contrato de serialização com o backend**, gravados em
 * `inputs.canal` e lidos pelo par Python. Eram `'assessor'`/`'consultor'` — um
 * segundo vocabulário para o mesmo conceito, que o agente do backend pegou
 * antes de custar um PDF incoerente (o valor não reconhecido cairia no ramo
 * "época antiga" e o documento sairia 119,52 com a tela em 134,52). Unificados
 * aqui em vez de traduzidos na borda: tradução é mais um lugar onde a
 * divergência pode voltar.
 */
export const CANAL_ASSESSORIA = 'assessoria'
export const CANAL_CONSULTORIA = 'consultoria_cvm'

// Fallback declarado ([D6]): na dúvida, o canal da assessoria — o que tem o
// custo maior para o cliente. Prometer o menor é o erro que não se conserta
// depois da conversa.
export const CANAL_PADRAO = CANAL_ASSESSORIA

const REGIME_POR_CANAL = {
  [CANAL_ASSESSORIA]: 'fee',
  [CANAL_CONSULTORIA]: 'cons',
}

/**
 * Como a tela DECLARA o regime que está comparando.
 *
 * [D8] O rótulo passou a nomear o CANAL, e agora isso é verdade: o canal é
 * escolha do cenário e move o número (a taxa de plataforma entra ou não no
 * total). Antes ele só podia falar de cashback — `fee_plat` estava fora dos
 * dois totais e o canal do cliente não era dado que existisse, então dizer
 * "canal" teria vendido a diferença errada.
 */
export const LABEL_POR_REGIME = {
  // ★ 11/08/2026: era "com a taxa de plataforma no custo do cliente". O custo
  // do cliente parou de depender do canal; o que a taxa muda é o destino.
  fee: 'assessor fee-based, com a taxa de plataforma retida de dentro do fee',
  cons: 'consultor CVM, sem taxa de plataforma e com o cashback integral',
}

/**
 * Regime do lado "depois" a partir do canal do cenário.
 *
 * Canal ausente ou desconhecido cai no default declarado — nunca num terceiro
 * comportamento. É o mesmo princípio do fallback da [D6]: na dúvida, o canal
 * de custo MAIOR para o cliente, porque prometer o menor é o erro que não se
 * conserta depois da conversa.
 */
export const regimeDoCanal = (canal) => {
  return REGIME_POR_CANAL[canal] || REGIME_POR_CANAL[CANAL_PADRAO]
}

/**
 * O canal, normalizado: sempre um dos dois valores do contrato.
 *
 * Existe porque o motor publica `canal` no resultado e o save o grava em
 * `inputs.canal` — deixar um `undefined` ou um valor estranho vazar para o
 * JSON faria o backend cair no ramo "época antiga" e o PDF divergir da tela.
 */
export const canalNormalizado = (canal) => {
  return REGIME_POR_CANAL[canal] ? canal : CANAL_PADRAO
}

export const REGIME_DEPOIS_PADRAO = regimeDoCanal(CANAL_PADRAO)

/**
 * Como a tela CHAMA quem atende o cliente, no canal do cenário.
 *
 * Owner, 2026-08-03: *"quando alteramos para consultor alterar assessor para
 * consultor e no card do consultor também"*. No canal de consultoria não
 * existe assessor — e o card rotulado "Consultor CVM" anunciando "receita do
 * assessor" é a contradição na cara de quem está apresentando ao cliente.
 *
 * Mora AQUI, ao lado das constantes de canal, e não num ternário por template:
 * hoje consomem o termo os cards de regime, a faixa de KPIs, o guia e o resumo
 * que vai para a oportunidade do CRM — e o PDF e o ensaio falam o mesmo
 * vocabulário. Uma cópia por superfície garante que a próxima nasça dizendo a
 * palavra errada.
 *
 * ⚠️ É o CARGO de quem atende, não o NOME DO REGIME. "Fee fixo · assessoria"
 * continua se chamando assim nos dois canais: nomeia o modelo fee-based da
 * assessoria, e não quem recebe a receita. Ao trocar um rótulo, a pergunta é
 * "isto nomeia o regime ou a pessoa?".
 */
const TERMO_DO_PROFISSIONAL = {
  [CANAL_ASSESSORIA]: 'assessor',
  [CANAL_CONSULTORIA]: 'consultor',
}

/**
 * O substantivo, minúsculo — a forma de "Receita do <termo>".
 *
 * Passa por `canalNormalizado`, então canal ausente ou desconhecido cai no
 * default declarado ([D6]) em vez de num terceiro comportamento: cenário salvo
 * antes do seletor não tem o campo, e ele não pode aparecer sem rótulo.
 */
export const termoDoProfissional = (canal) => {
  return TERMO_DO_PROFISSIONAL[canalNormalizado(canal)]
}

/**
 * A mesma palavra abrindo frase ("Ganho Consultor: R$ …", no resumo que vai
 * para a observação da oportunidade). Capitalizar na superfície seria a
 * segunda cópia da regra, que é justamente o que esta fonte única evita.
 */
export const termoDoProfissionalCapitalizado = (canal) => {
  const termo = termoDoProfissional(canal)
  return termo.charAt(0).toUpperCase() + termo.slice(1)
}
